import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Heart } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

interface Product {
  id: number;
  name: string;
  slug: string;
  price: number;
  compare_price?: number;
  featured_image: string;
  is_new?: boolean;
  is_featured?: boolean;
  discount_percentage?: number;
  colors?: Array<{ name: string; hex: string }>;
  sizes?: string[];
  category?: { name: string; slug: string };
}

interface ProductCardProps {
  product: Product;
  className?: string;
}

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=400&q=70';

export default function ProductCard({ product, className = '' }: ProductCardProps) {
  const { addItem } = useCart();
  const [isWished, setIsWished] = useState(false);
  const [imageError, setImageError] = useState(false);

  const discount = product.compare_price && product.compare_price > product.price
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100)
    : 0;

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      id: Date.now(),
      productId: product.id,
      name: product.name,
      price: product.price,
      image: getImageUrl(product.featured_image) || FALLBACK_IMAGE,
      quantity: 1,
    });
    toast.success(`Added to cart!`, { duration: 2000 });
  };

  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWished(w => !w);
    toast.success(isWished ? 'Removed from wishlist' : 'Added to wishlist', { duration: 1500 });
  };

  const formatPrice = (price: number) => `Rs.${price.toLocaleString()}`;
  const imgSrc = imageError ? FALLBACK_IMAGE : (getImageUrl(product.featured_image) || FALLBACK_IMAGE);

  return (
    <div className={`group relative ${className}`}>
      <Link to={`/products/${product.slug}`} className="block">
        {/* Image Container */}
        <div className="relative overflow-hidden bg-gray-50 aspect-[3/4] rounded-sm">
          <img
            src={imgSrc}
            alt={product.name}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />

          {/* Gradient overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.is_new && (
              <span className="bg-gray-900 text-white text-[10px] font-semibold px-2 py-0.5 tracking-wider">NEW</span>
            )}
            {discount > 0 && (
              <span className="bg-red-600 text-white text-[10px] font-semibold px-2 py-0.5">-{discount}%</span>
            )}
          </div>

          {/* Wishlist Button */}
          <button
            onClick={handleWishlist}
            className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
            title={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart size={15} className={isWished ? 'fill-red-500 text-red-500' : 'text-gray-600'} />
          </button>

          {/* Color Swatches — hover reveal */}
          {product.colors && product.colors.length > 0 && (
            <div className="absolute bottom-14 left-0 right-0 flex justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {product.colors.slice(0, 6).map((color, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full border-2 border-white shadow-md hover:scale-125 transition-transform cursor-pointer"
                  style={{ backgroundColor: color.hex }}
                  title={color.name}
                />
              ))}
            </div>
          )}

          {/* Quick Add Button */}
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <button
              onClick={handleQuickAdd}
              className="w-full bg-gray-900/95 backdrop-blur-sm text-white py-3 text-[11px] font-semibold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
            >
              <ShoppingBag size={13} />
              Quick Add
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="mt-3 px-0.5">
          {product.category && (
            <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-medium">{product.category.name}</p>
          )}
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug group-hover:text-gray-600 transition-colors">
            {product.name}
          </h3>
          <div className="flex items-center gap-2 mt-1.5">
            {product.compare_price && product.compare_price > product.price && (
              <span className="text-xs text-gray-400 line-through">{formatPrice(product.compare_price)}</span>
            )}
            <span className={`text-sm font-bold ${discount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {formatPrice(product.price)}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}


interface Product {
  id: number;
  name: string;
  slug: string;
  price: number;
  compare_price?: number;
  featured_image: string;
  is_new?: boolean;
  is_featured?: boolean;
  discount_percentage?: number;
  colors?: Array<{ name: string; hex: string }>;
  sizes?: string[];
  category?: { name: string; slug: string };
}

interface ProductCardProps {
  product: Product;
  className?: string;
}

